import {
  DEFAULT_THRESHOLDS,
  screwBand,
  type ScrewState,
} from "../../../utils/screwSpeed";
import type { ComponentState, StateRule } from "./ProductionLineDiagram";
import type { ExtruderSettings } from "../../../api/extruder";
import type { FoamSettings } from "../../../api/foamSettings";

/**
 * How a raw plant reading becomes a color on the overview diagrams.
 *
 * Kept apart from the pages so the rules can be tested directly: every one of
 * them was wrong at some point, and each mistake read as "running" — the most
 * expensive direction to be wrong in on a plant status board.
 *
 * The rules themselves are the plant's, from the state/color matrix:
 *
 *   green   normal running
 *   orange  running, but outside the normal band — worth a look
 *   red     stopped, unreachable, or past a safety limit
 *   grey    we do not know: no reading, or no configured band to judge against
 *
 * Grey is deliberately not a fourth severity. It means the rule could not be
 * evaluated, and it must never be confused with a healthy green.
 */

/**
 * Severity order for combining two rules about the same component. Green is
 * lowest, so `worst()` only ever moves a component towards alarm, and an
 * unevaluated rule (grey) outranks a healthy one — a component is not green
 * on the strength of half its signals.
 */
const SEVERITY: Record<ComponentState, number> = {
  running: 0,
  idle: 1,
  off: 2,
  unknown: 3,
  warning: 4,
  error: 5,
};

/** The most alarming of several rules about one component. */
export const worst = (
  first: ComponentState,
  ...rest: ComponentState[]
): ComponentState =>
  rest.reduce((acc, s) => (SEVERITY[s] > SEVERITY[acc] ? s : acc), first);

// ---- Blender ----
/**
 * Blender color, per the matrix: running → green, stopped → red, and an
 * unreachable PLC → red.
 *
 * `stale` is the unreachable signal. It is computed server-side from how long
 * since plc-worker last *read* the PLC (not from row age — the worker skips
 * unchanged readings, so a quiet blender is normal), which makes it exactly
 * the matrix's "network response: unreachable".
 *
 * Idle stays green: it is a healthy pause between batches, the matrix has no
 * row for it, and operators read amber as a problem. The chip still says
 * "Idle", so the two are tellable apart without alarming on one.
 */
export const blenderState = (b?: {
  status?: string;
  stale?: boolean;
}): ComponentState => {
  if (!b) return "unknown";
  if (b.stale) return "error";
  if (b.status === "running") return "running";
  if (b.status === "idle") return "idle";
  if (b.status === "stopped") return "error";
  return "unknown";
};

export const boolState = (b?: boolean | null): ComponentState =>
  b == null ? "unknown" : b ? "running" : "off";

// ---- Extruder ----
// Thresholds live on the Settings page (/settings/extruders) so the plant can
// tune them, and the bands themselves live in utils/screwSpeed so the diagrams
// and the extruder dashboards judge a speed the same way. Re-exported here
// because this module's callers have always taken them from it.
export { DEFAULT_THRESHOLDS };

/**
 * An extruder's state from its screw speed, in the same three bands the
 * extruder dashboards use: at or above `running_min` → green, at or above
 * `idle_min` → orange (turning, but not at production speed), below that →
 * red, including a screw barely creeping.
 *
 * This used to test `running_min` alone, so anything turning at all was orange.
 * That split the two places a speed is judged: the same reading could be an
 * orange machine on the overview and a "stopped" run event on its dashboard.
 * One rule, in utils/screwSpeed, now serves both.
 *
 * A tripped heater fault bit is orange, so it can only pull a green band down;
 * it never softens a band that is already worse. A missing reading is grey:
 * "no speed reported" is not the same claim as "speed is zero".
 *
 * The in/out flag this used to read is carriage position, not motion: an
 * extruder parked "in" with a stopped screw was being drawn as running.
 */
/** How the shared screw bands paint on a diagram: idle is the orange
 *  "turning, but not at production speed", stopped is red. */
const DIAGRAM_COLOR: Record<ScrewState, ComponentState> = {
  running: "running",
  idle: "warning",
  stopped: "error",
};

export const speedState = (
  hz?: number | null,
  faults?: number[] | boolean[] | null,
  t: ExtruderSettings = DEFAULT_THRESHOLDS
): ComponentState => {
  const band: ComponentState =
    hz == null ? "unknown" : DIAGRAM_COLOR[screwBand(hz, t)];
  const tripped = Array.isArray(faults) && faults.some(Boolean);
  return tripped ? worst(band, "warning") : band;
};

/**
 * Extruder carriage position: in → green, out → orange.
 *
 * Position is not motion — an extruder parked "in" with a stopped screw is not
 * running — so this is only ever one of the rows `extruderState` combines, not
 * a verdict on its own. Lamination 1 reports it as "in"/"out", Lamination 2 as
 * a boolean where true is in.
 */
export const positionState = (
  p?: boolean | string | null
): ComponentState => {
  if (p == null) return "unknown";
  if (typeof p === "string") {
    if (p === "in") return "running";
    if (p === "out") return "warning";
    return "unknown";
  }
  return p ? "running" : "warning";
};

/**
 * One extruder, from all four rows the matrix gives it: carriage position,
 * screw speed, heater fault bits, and whether we are reaching the PLC at all.
 *
 * Unreachable outranks the rest — none of the other three are current if the
 * worker has stopped reading the PLC, so they are not worth combining.
 * Otherwise the most alarming row wins, which means an extruder parked out is
 * usually red for its stopped screw rather than orange for being out; orange
 * shows when the carriage is out while the screw is still turning.
 */
export const extruderState = (
  e: {
    speed?: number | null;
    position?: boolean | string | null;
    faults?: number[] | boolean[] | null;
    stale?: boolean;
  },
  t: ExtruderSettings = DEFAULT_THRESHOLDS
): ComponentState => {
  if (e.stale) return "error";
  return worst(speedState(e.speed, e.faults, t), positionState(e.position));
};

/**
 * Densifier: running → green, stopped → red, any tripped heater fault bit →
 * orange on top.
 *
 * The matrix gives the densifier no network row, so an unreachable PLC leaves
 * it grey rather than inventing a color — unlike the extruders beside it,
 * which the matrix does say to redden.
 */
export const densifierState = (d?: {
  running?: boolean | null;
  faults?: number[] | boolean[] | null;
  stale?: boolean;
}): ComponentState => {
  if (!d || d.stale || d.running == null) return "unknown";
  const base: ComponentState = d.running ? "running" : "error";
  const tripped = Array.isArray(d.faults) && d.faults.some(Boolean);
  return tripped ? worst(base, "warning") : base;
};

/**
 * The line element: a roll-up of every machine drawn on that diagram.
 *
 * Green only while the whole line is healthy, and it inherits the matrix's
 * rules for free — anything red on the line reddens it, anything orange
 * ambers it.
 *
 * Parts with no signal at all are skipped rather than counted. Lamination 1
 * reports no densifier, and letting an absent machine outrank a working one
 * would leave that line permanently grey. A line where nothing at all is
 * readable is itself grey.
 */
export const lineState = (...parts: ComponentState[]): ComponentState => {
  const known = parts.filter((p) => p !== "unknown");
  if (!known.length) return "unknown";
  return worst(known[0], ...known.slice(1));
};

// ---- Foam: pressure and temperature bands ----
/**
 * No thresholds configured. Lives here rather than with the API client so the
 * rules stay importable without pulling axios in, and so "unset" is defined
 * next to the code that has to treat it as grey.
 */
export const EMPTY_FOAM_SETTINGS: FoamSettings = {
  hp_running_min: null,
  hp_running_max: null,
  hp_safety_max: null,
  lp_running_min: null,
  lp_running_max: null,
  temp_min: null,
  temp_max: null,
};


/**
 * In-band → green, outside → `outside`. A missing reading or an unset limit
 * is grey: there is no band to judge against, so there is nothing to claim.
 *
 * Bounds are inclusive. The matrix writes these as strict inequalities, but a
 * reading sitting exactly on a configured limit is the limit being met, not
 * exceeded, and flapping a diagram on the boundary helps nobody.
 */
const bandState = (
  v: number | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
  outside: ComponentState
): ComponentState => {
  if (v == null || min == null || max == null) return "unknown";
  return v >= min && v <= max ? "running" : outside;
};

/**
 * Butane high pressure side. Past the safety limit is the only red; merely
 * outside the running band — under it, or over it but still under safety — is
 * orange.
 */
export const highPressureState = (
  psi: number | null | undefined,
  s: FoamSettings
): ComponentState => {
  if (psi != null && s.hp_safety_max != null && psi > s.hp_safety_max)
    return "error";
  return bandState(psi, s.hp_running_min, s.hp_running_max, "warning");
};

/** Butane low pressure side: in band or red. The matrix gives it no orange. */
export const lowPressureState = (
  psi: number | null | undefined,
  s: FoamSettings
): ComponentState =>
  bandState(psi, s.lp_running_min, s.lp_running_max, "error");

/** Cooling water: in band or orange. The matrix gives it no red. */
export const coolingWaterState = (
  temp: number | null | undefined,
  s: FoamSettings
): ComponentState => bandState(temp, s.temp_min, s.temp_max, "warning");

/** Butane on/off: on → green, off → red. */
export const butaneState = (on?: boolean | null): ComponentState =>
  on == null ? "unknown" : on ? "running" : "error";

// ---- Winders ----
/**
 * Foam winder: auto → green, manual → orange, stopped → red.
 *
 * Running-but-mode-unknown is grey rather than green, because the foam matrix
 * ties green specifically to auto. A stale row is grey too: the winder worker
 * writes every poll, so a row past the cutoff means we are not hearing from
 * it, not that it stopped.
 */
export const foamWinderState = (w?: {
  status?: boolean | null;
  auto_mode?: boolean | null;
  stale?: boolean;
}): ComponentState => {
  if (!w || w.stale || w.status == null) return "unknown";
  if (!w.status) return "error";
  if (w.auto_mode == null) return "unknown";
  return w.auto_mode ? "running" : "warning";
};

/**
 * Lamination 2's winder: running → green, stopped → red.
 *
 * The lamination matrix has no manual column and the PLC reports a plain
 * running bit with no auto/manual flag, so "running auto" is simply true —
 * this is not the foam winder's three-way rule.
 *
 * Like the densifier beside it, the lamination matrix gives the winder no
 * network row, so an unreachable PLC leaves it grey rather than red.
 */
export const lamWinderState = (w?: {
  running?: boolean | null;
  stale?: boolean;
}): ComponentState => {
  if (!w || w.stale || w.running == null) return "unknown";
  return w.running ? "running" : "error";
};

// ---- Why a component is the color it is ----
/**
 * The rules above, restated for the person looking at the diagram.
 *
 * Each set is the legend the hover card shows, ordered least to most alarming,
 * with the row that fired highlighted — so a red machine explains itself
 * without anyone opening this file. They live here, beside the functions they
 * describe, because prose that drifts from its rule is worse than no prose:
 * the tests assert every state a function can return has a row covering it.
 *
 * Written for the operator standing at the line, not for whoever maintains
 * this module — plant words, not field names.
 */

/** A configured limit, or an em dash where the Settings page left it unset. */
const limit = (v: number | null | undefined, unit: string): string =>
  v == null ? "—" : `${v}${unit}`;

export const BLENDER_RULES: StateRule[] = [
  { state: "running", when: "Dosing." },
  {
    state: "idle",
    when: "Powered but between batches — a healthy pause, so it stays green.",
  },
  { state: "error", when: "Stopped, or its PLC has stopped answering." },
  { state: "unknown", when: "No reading to judge." },
];

export const extruderRules = (
  t: ExtruderSettings = DEFAULT_THRESHOLDS
): StateRule[] => [
  { state: "running", when: `Screw at or above the ${t.running_min} Hz run speed.` },
  {
    state: "warning",
    when: `Screw turning but under ${t.running_min} Hz, or the carriage is out, or a heater fault bit is tripped.`,
  },
  { state: "error", when: "Screw stopped, or its PLC has stopped answering." },
  { state: "unknown", when: "No screw speed reported." },
];

export const DENSIFIER_RULES: StateRule[] = [
  { state: "running", when: "Running." },
  { state: "warning", when: "Running with a fault bit tripped." },
  { state: "error", when: "Stopped." },
  {
    state: "unknown",
    when: "No reading — including an unreachable PLC, which this machine has no rule to redden for.",
  },
];

export const LAM_WINDER_RULES: StateRule[] = [
  { state: "running", when: "Winding — the web is moving." },
  { state: "error", when: "Stopped." },
  {
    state: "unknown",
    when: "No reading, or its PLC has stopped answering.",
  },
];

export const FOAM_WINDER_RULES: StateRule[] = [
  { state: "running", when: "Running in auto." },
  { state: "warning", when: "Running, but on manual control." },
  { state: "error", when: "Stopped." },
  {
    state: "unknown",
    when: "No reading, an unreported mode, or the winder PLC has gone quiet.",
  },
];

export const highPressureRules = (s: FoamSettings): StateRule[] => [
  {
    state: "running",
    when: `Butane on and the high side between ${limit(
      s.hp_running_min,
      ""
    )} and ${limit(s.hp_running_max, " psi")}.`,
  },
  {
    state: "warning",
    when: `Outside that band but still under the ${limit(
      s.hp_safety_max,
      " psi"
    )} safety limit.`,
  },
  {
    state: "error",
    when: `Past the ${limit(s.hp_safety_max, " psi")} safety limit, or butane is off.`,
  },
  {
    state: "unknown",
    when: "No pressure reading, or no band set on the Settings page.",
  },
];

export const lowPressureRules = (s: FoamSettings): StateRule[] => [
  {
    state: "running",
    when: `Low side between ${limit(s.lp_running_min, "")} and ${limit(
      s.lp_running_max,
      " psi"
    )}.`,
  },
  { state: "error", when: "Outside that band. This side has no warning band." },
  {
    state: "unknown",
    when: "No pressure reading, or no band set on the Settings page.",
  },
];

export const coolingWaterRules = (s: FoamSettings): StateRule[] => [
  {
    state: "running",
    when: `Water between ${limit(s.temp_min, "")} and ${limit(
      s.temp_max,
      "\u00b0C"
    )}.`,
  },
  { state: "warning", when: "Outside that band. This reading has no red." },
  {
    state: "unknown",
    when: "No temperature reading, or no band set on the Settings page.",
  },
];

/** The roll-up element, wherever the line is drawn from its machines. */
export const LINE_ROLLUP_RULES: StateRule[] = [
  { state: "running", when: "Every machine on this diagram is healthy." },
  { state: "idle", when: "Nothing is wrong, and something on the line is idle." },
  { state: "warning", when: "Something on the line is in warning." },
  { state: "error", when: "Something on the line is stopped or unreachable." },
  { state: "unknown", when: "Nothing on the line is reporting." },
];

/** A machine that is drawn but has no signal behind it at all. */
export const NOT_INSTRUMENTED_RULES: StateRule[] = [
  {
    state: "unknown",
    when: "Not instrumented — the PLC reports nothing for this machine, so it has no color to take.",
  },
];
